import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Stack, TextField, MenuItem, Typography, Chip, Tooltip, TablePagination,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, InputAdornment,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockIcon from '@mui/icons-material/Lock';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';

const ACTION_COLORS = {
  CREATE:  'success',
  UPDATE:  'warning',
  DELETE:  'error',
  LOGIN:   'info',
  LOGOUT:  'default',
  EXPORT:  'info',
  APPROVE: 'success',
  REJECT:  'error',
  VIEW:    'default',
};

const ENTITY_LABELS = {
  employee:   'Punëtor',
  contract:   'Kontratë',
  payroll:    'Pagë',
  leave:      'Pushim',
  fiscal:     'Politikë Fiskale',
  user:       'Përdorues',
  workers:    'Punëtor',
  workerContract: 'Kontratë',
};

const ACTION_LABELS = {
  CREATE:  'Krijoi',
  UPDATE:  'Ndryshoi',
  DELETE:  'Fshiu',
  LOGIN:   'Hyrje',
  LOGOUT:  'Dalje',
  EXPORT:  'Eksportoi',
  APPROVE: 'Aprovoi',
  REJECT:  'Refuzoi',
  VIEW:    'Shikoi',
};

function fmt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('sq-AL', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
}

export default function AuditLog() {
  const [logs, setLogs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [q, setQ]               = useState('');
  const [actionF, setActionF]   = useState('');
  const [entityF, setEntityF]   = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [page, setPage]         = useState(0);
  const [rpp, setRpp]           = useState(25);
  const [detail, setDetail]     = useState(null);

  useEffect(() => {
    setLoading(true);
    apiService().query('getAuditLog', '').then(r => {
      if (r?.success) setLogs(r.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let data = logs;
    if (actionF) data = data.filter(x => x.Action === actionF);
    if (entityF) data = data.filter(x => x.EntityType === entityF);
    if (dateFrom) data = data.filter(x => x.CreatedAt >= dateFrom);
    if (dateTo)   data = data.filter(x => x.CreatedAt <= dateTo + 'T23:59:59');
    if (q) {
      const term = q.toLowerCase();
      data = data.filter(x =>
        [x.EventType, x.UserEmail, x.EntityType, x.Action]
          .filter(Boolean).map(String).map(s => s.toLowerCase())
          .some(s => s.includes(term))
      );
    }
    return data;
  }, [logs, q, actionF, entityF, dateFrom, dateTo]);

  const exportCsv = () => {
    const headers = ['ID', 'Data/Ora', 'Veprimi', 'Entiteti', 'Entiteti ID', 'Tipi ngjarjes', 'Përdoruesi', 'IP'];
    const lines = [headers.join(',')];
    filtered.forEach(l => {
      const row = [
        l.Id, fmt(l.CreatedAt), l.Action, l.EntityType, l.EntityId,
        l.EventType, l.UserEmail, l.UserIp,
      ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`);
      lines.push(row.join(','));
    });
    const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = `audit_log_${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  const exportPdf = () => {
    const { jsPDF } = require('jspdf');
    const doc = new jsPDF('l', '', 'a4');
    let y = 12;
    doc.setFontSize(12);
    doc.text('Audit Log — Nexuma', 10, y); y += 8;
    doc.setFontSize(8);
    const cols = ['ID', 'Data/Ora', 'Veprimi', 'Entiteti', 'Tipi ngjarjes', 'Përdoruesi'];
    const widths = [10, 35, 22, 22, 55, 50];
    let x = 10;
    cols.forEach((c, i) => { doc.text(c, x, y); x += widths[i]; }); y += 6;
    filtered.slice(0, 500).forEach(l => {
      if (y > 185) { doc.addPage(); y = 12; }
      x = 10;
      const row = [String(l.Id), fmt(l.CreatedAt), l.Action || '', l.EntityType || '', l.EventType || '', l.UserEmail || ''];
      row.forEach((c, i) => { doc.text(String(c).slice(0, widths[i] / 2.3), x, y); x += widths[i]; });
      y += 5;
    });
    doc.save(`audit_log_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  const uniqueActions  = [...new Set(logs.map(x => x.Action).filter(Boolean))];
  const uniqueEntities = [...new Set(logs.map(x => x.EntityType).filter(Boolean))];

  return (
    <Box>
      <PageHeader
        title="Audit Log — Regjistri i Aktiviteteve"
        actions={
          <Stack direction="row" spacing={1}>
            <Button startIcon={<DownloadIcon />} variant="outlined" onClick={exportCsv}>CSV</Button>
            <Button startIcon={<DownloadIcon />} variant="outlined" onClick={exportPdf}>PDF</Button>
          </Stack>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }} icon={<LockIcon />}>
        Ky regjistër është <strong>i pandryshueshëm (immutable)</strong>. Asnjë rekord nuk mund të fshihet.
        Retencion: <strong>7 vjet</strong> (LP + ATK). Gjithsej: <strong>{logs.length}</strong> ngjarje.
      </Alert>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
            <TextField
              size="small" placeholder="Kërko ngjarje, user, entitet..."
              value={q} onChange={e => setQ(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              sx={{ minWidth: 220 }}
            />
            <TextField select size="small" label="Veprimi" value={actionF} onChange={e => setActionF(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value="">Të gjitha</MenuItem>
              {uniqueActions.map(a => <MenuItem key={a} value={a}>{ACTION_LABELS[a] || a}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Entiteti" value={entityF} onChange={e => setEntityF(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value="">Të gjitha</MenuItem>
              {uniqueEntities.map(e => <MenuItem key={e} value={e}>{ENTITY_LABELS[e] || e}</MenuItem>)}
            </TextField>
            <TextField size="small" type="date" label="Nga" InputLabelProps={{ shrink: true }}
              value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            <TextField size="small" type="date" label="Deri" InputLabelProps={{ shrink: true }}
              value={dateTo} onChange={e => setDateTo(e.target.value)} />
            <Button size="small" onClick={() => { setQ(''); setActionF(''); setEntityF(''); setDateFrom(''); setDateTo(''); }}>
              Pastro
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Shfaqen {filtered.length} nga {logs.length} ngjarje
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Data / Ora</TableCell>
                <TableCell>Veprimi</TableCell>
                <TableCell>Entiteti</TableCell>
                <TableCell>ID Entitetit</TableCell>
                <TableCell>Tipi i Ngjarjes</TableCell>
                <TableCell>Përdoruesi</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>Detaje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} align="center">Duke ngarkuar…</TableCell></TableRow>
              ) : filtered.slice(page * rpp, page * rpp + rpp).map(l => (
                <TableRow key={l.Id} hover>
                  <TableCell><Typography variant="caption" fontFamily="monospace">{l.Id}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{fmt(l.CreatedAt)}</Typography></TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={ACTION_LABELS[l.Action] || l.Action}
                      color={ACTION_COLORS[l.Action] || 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{ENTITY_LABELS[l.EntityType] || l.EntityType || '—'}</TableCell>
                  <TableCell><Typography variant="caption" fontFamily="monospace">{l.EntityId || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{l.EventType || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{l.UserEmail || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="caption" fontFamily="monospace">{l.UserIp || '—'}</Typography></TableCell>
                  <TableCell>
                    <Tooltip title="Shiko detajet">
                      <span>
                        <Button size="small" onClick={() => setDetail(l)}>
                          <VisibilityIcon fontSize="small" />
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            rowsPerPage={rpp}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={e => { setRpp(+e.target.value); setPage(0); }}
          />
        </CardContent>
      </Card>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Detajet e Ngjarjes — ID: {detail?.Id}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2"><strong>Tipi:</strong> {detail?.EventType}</Typography>
            <Typography variant="body2"><strong>Veprimi:</strong> {detail?.Action}</Typography>
            <Typography variant="body2"><strong>Data / Ora:</strong> {fmt(detail?.CreatedAt)}</Typography>
            <Typography variant="body2"><strong>Përdoruesi:</strong> {detail?.UserEmail} ({detail?.UserIp})</Typography>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Gjendja PARA:</Typography>
            <Box sx={{ bgcolor: 'background.default', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
              {detail?.BeforeData ? JSON.stringify(detail.BeforeData, null, 2) : 'N/A'}
            </Box>
            <Typography variant="subtitle2">Gjendja PAS:</Typography>
            <Box sx={{ bgcolor: 'background.default', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
              {detail?.AfterData ? JSON.stringify(detail.AfterData, null, 2) : 'N/A'}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Mbyll</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
